import React, { useEffect, useState } from "react";

import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import CloseIcon from "@mui/icons-material/Close";
import {
  Autocomplete,
  Button,
  CircularProgress,
  IconButton,
  Paper,
  Stack,
  StackProps,
  styled,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography
} from "@mui/material";
import { CreateDealContent } from "components/Prompts/CreateDealContent";
import { MaterialTooltip } from "components/tooltips/MaterialTooltip";
import { useActionHook, useCanCreateDeal, usePrompt } from "hooks";
import { useAccountSelector, useDealActions, useDealStatusesSelector } from "store";

const TableCellStyled = styled(TableCell)(() => ({
  padding: "6px 10px",
  minWidth: "100px",
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
  "&:first-of-type": {
    width: "40%"
  },
  "&:not(:first-of-type)": {
    width: "auto"
  }
}));

interface IFilesValues {
  id: number | null;
  deal_id: number | null;
  name: string;
  inProgress: boolean;
  file: File;
}

interface Props {
  files: FileList;
  onCancel: () => void;
  onSubmit: (data: Record<number, FileList>) => void;
}

export const UploadFilesContent: React.FC<Props> = ({ files, onCancel, onSubmit }) => {
  const { getDealFromFileNameAction, getDealsTableRowsAction } = useDealActions();
  const [filesValues, setFilesValues] = useState<IFilesValues[]>([]);
  const statuses = useDealStatusesSelector().map((status) => status.name);
  const openPrompt = usePrompt();
  const isFreemiumMode = useAccountSelector()?.is_freemium || false;
  const canCreateDeal = useCanCreateDeal();
  const [dealNotFound, setDealNotFound] = useState<number[]>([]);

  const { data: deals = [], action: getDeals } = useActionHook(() =>
    getDealsTableRowsAction({
      filters: {
        "Deal Status": statuses
      }
    })
  );

  const onSubmitHandler = (filesValues: IFilesValues[]) => {
    const result = filesValues.reduce(
      (acc, file) => {
        if (!file.deal_id) {
          return acc;
        }
        if (!acc[file.deal_id]) {
          acc[file.deal_id] = new DataTransfer().files;
        }
        const dt = new DataTransfer();
        Array.from(acc[file.deal_id]).forEach((f) => dt.items.add(f));
        dt.items.add(file.file);
        acc[file.deal_id] = dt.files;
        return acc;
      },
      {} as Record<number, FileList>
    );

    onSubmit(result);
  };

  useEffect(() => {
    setFilesValues(
      Array.from(files)
        .sort((a, b) => a.name.localeCompare(b.name))
        .map((file) => {
          return {
            id: Math.floor(Math.random() * 10000),
            name: file.name,
            deal_id: null,
            inProgress: false,
            file: file
          };
        })
    );
  }, [files]);

  useEffect(() => {
    getDeals();
  }, []);

  const onGetDealFromFileName = async (fileName: string, fileId: number) => {
    setFilesValues((prevState) => prevState.map((item) => (item.id === fileId ? { ...item, inProgress: true } : item)));

    const deal = await getDealFromFileNameAction({ file_name: fileName });
    if (!deal) {
      setDealNotFound((prevState) => [...prevState, fileId]);
    }

    setFilesValues((prevState) =>
      prevState.map((item) =>
        item.id === fileId ? { ...item, inProgress: false, ...(deal && { deal_id: deal.id }) } : item
      )
    );
  };

  const handleCreateNewDeal = async (fileId: number) => {
    const result = await openPrompt({
      Component: CreateDealContent
    });

    if (result) {
      // Refresh deals list after creating new deal
      await getDeals();

      // Set the newly created deal to the file
      setFilesValues((prevState) =>
        prevState.map((item) =>
          item.id === fileId
            ? {
                ...item,
                deal_id: result.dealId
              }
            : item
        )
      );
    }
  };

  return (
    <Stack sx={containerStyle}>
      <Stack
        direction="row"
        sx={{ justifyContent: "space-between", alignItems: "center", pb: "8px", borderBottom: "1px solid #DBE8F5" }}
      >
        <Typography>Upload files</Typography>
        <IconButton size="sm" onClick={onCancel}>
          <CloseIcon />
        </IconButton>
      </Stack>

      <Stack
        sx={{
          p: "5px 10px",
          backgroundColor: "#FDF8F5",
          width: "100%"
        }}
      >
        <Typography sx={{ fontSize: "16px", fontWeight: 700, color: "#496682" }}>AI Extraction</Typography>
        <Typography sx={{ fontSize: "12px", fontWeight: 400, color: "#496682" }}>
          Select a deal to link the uploaded file(s).
        </Typography>
        <Typography sx={{ fontSize: "12px", fontWeight: 400, color: "#496682" }}>
          Need help? Press AI Assist and the system will suggest possible matches.
        </Typography>
        <Typography sx={{ fontSize: "12px", fontWeight: 400, color: "#496682" }}>
          Once you’ve chosen a deal, press Done to start AI value extraction.
        </Typography>
      </Stack>

      <TableContainer sx={{ width: "100%", maxHeight: 400, overflow: "auto" }}>
        <Table sx={{ tableLayout: "fixed" }} stickyHeader>
          <TableHead sx={{ bgcolor: "#F6F8FC" }}>
            <TableRow>
              <TableCellStyled>File name</TableCellStyled>
              <TableCellStyled>Deal</TableCellStyled>
              <TableCellStyled sx={{ width: "50px !important" }}></TableCellStyled>
            </TableRow>
          </TableHead>
          <TableBody>
            {filesValues.map((file, index) => {
              return (
                <TableRow key={index}>
                  <TableCellStyled
                    sx={{
                      maxWidth: "130px",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis"
                    }}
                  >
                    <Stack>
                      <MaterialTooltip title={file.name}>
                        <Typography
                          sx={{
                            fontSize: "14px",
                            fontWeight: 500,
                            overflow: "hidden",
                            textOverflow: "ellipsis"
                          }}
                        >
                          {file.name}
                        </Typography>
                      </MaterialTooltip>
                    </Stack>
                  </TableCellStyled>

                  <TableCellStyled>
                    <Autocomplete
                      getOptionLabel={(option) => option.title}
                      options={deals}
                      value={deals.find((deal) => deal.id === file.deal_id) || null}
                      onChange={(_, option) => {
                        setDealNotFound((prevState) => prevState.filter((id) => id !== file.id));
                        setFilesValues((prevState) =>
                          prevState.map((item) =>
                            item.id === file.id
                              ? {
                                  ...item,
                                  deal_id: option?.id || null
                                }
                              : item
                          )
                        );
                      }}
                      PaperComponent={({ children }) => {
                        return (
                          <Paper
                            sx={{
                              position: "absolute",
                              backgroundColor: "white",
                              width: "100%",
                              borderRadius: "10px",
                              overflow: "hidden"
                            }}
                          >
                            <>
                              <Stack
                                sx={{
                                  maxHeight: "250px",
                                  overflow: "auto",
                                  "& .MuiAutocomplete-option:hover": {
                                    backgroundColor: "#edf1f9"
                                  }
                                }}
                              >
                                {children}
                              </Stack>
                              {canCreateDeal && (
                                <Button
                                  onMouseDown={async () => {
                                    file.id && (await handleCreateNewDeal(file.id));
                                  }}
                                  sx={{
                                    display: "flex",
                                    justifyContent: "flex-start",
                                    height: "50px",
                                    backgroundColor: "#fff",
                                    boxShadow: "-2px 2px 15px -2px rgba(0, 0, 0, 0.25)",
                                    borderRadius: "0 0 10px 10px"
                                  }}
                                  fullWidth
                                >
                                  Add Deal
                                </Button>
                              )}
                            </>
                          </Paper>
                        );
                      }}
                      renderOption={(props, item) => (
                        <li {...props} key={item.id}>
                          {item.title}
                        </li>
                      )}
                      renderInput={(params) => (
                        <TextField
                          sx={{
                            marginBottom: "10px",
                            ".MuiFormHelperText-root": {
                              bottom: "-17px",
                              position: "absolute",
                              fontSize: "11px",
                              marginLeft: "0px"
                            }
                          }}
                          {...params}
                          placeholder="Select"
                          error={dealNotFound.includes(file.id!)}
                          helperText={
                            dealNotFound.includes(file.id!) &&
                            "No matching Deal found. Select a Deal or create a new one"
                          }
                        />
                      )}
                    />
                  </TableCellStyled>
                  {!isFreemiumMode && (
                    <TableCell sx={{ width: "50px !important", padding: "0px !important" }}>
                      {file.inProgress ? (
                        <CircularProgress size={25} />
                      ) : (
                        <MaterialTooltip title="Fill deal from file name" isShow>
                          <IconButton
                            sx={{
                              marginBottom: "10px"
                            }}
                            size="sm"
                            onClick={() => onGetDealFromFileName(file.name, file.id!)}
                            disabled={file.inProgress}
                          >
                            <AutoFixHighIcon sx={{ width: "25px", height: "25px" }} />
                          </IconButton>
                        </MaterialTooltip>
                      )}
                    </TableCell>
                  )}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <Stack direction="row" sx={{ justifyContent: "flex-end", gap: "8px" }}>
        <Button variant="outlined" onClick={onCancel}>
          Close
        </Button>
        <Button
          disabled={filesValues.some((file) => !file.deal_id)}
          variant="contained"
          onClick={() => onSubmitHandler(filesValues)}
        >
          Done
        </Button>
      </Stack>
    </Stack>
  );
};

const containerStyle: StackProps["sx"] = {
  position: "absolute" as "absolute",
  display: "flex",
  flexDirection: "column",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  outline: "none",
  bgcolor: "white",
  width: "752px",
  p: "32px",
  gap: "20px",
  borderRadius: "8px",
  boxShadow:
    "0px 2px 4px -1px rgba(0, 0, 0, 0.20), 0px 4px 5px 0px rgba(0, 0, 0, 0.14), 0px 1px 10px 0px rgba(0, 0, 0, 0.12)"
};
